// src/pages/ResetPassword.js (Client)
// Completes the emailed password-reset flow: /reset-password?token=...
// Tokens are minted by the backend passwordController (purpose: 'pwreset')
// and consumed via POST /api/reset-password.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ForgotPassword.css";
import "./ResetPassword.css";

import passwordImage from "../assets/password.jpg";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const MIN_PASSWORD_LENGTH = 8;

function readTokenFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  } catch {
    return "";
  }
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const token = useMemo(readTokenFromLocation, []);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const tooShort =
    password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage("");

    if (!password) {
      setErrorMessage("Password is required.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
      );
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data?.ok) {
        setSucceeded(true);
      } else {
        setErrorMessage(
          data?.error ||
            "This reset link is invalid or has expired. Please request a new one."
        );
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const heroBadge = (
    <div className="forgot-password-hero-badge-wrapper">
      <img
        src={passwordImage}
        alt="Reset Password"
        className="forgot-password-hero-img"
      />
    </div>
  );

  // Missing token: the emailed link is malformed or was truncated.
  if (!token) {
    return (
      <div className="forgot-password-container reset-password-page">
        {heroBadge}
        <h1>Reset Password</h1>
        <div className="auth-inline-message is-error">
          This reset link is missing its token. Please request a new reset
          email.
        </div>
        <button
          className="back-to-login"
          type="button"
          onClick={() => navigate("/forgot-password")}
        >
          Request a new reset link
        </button>
        <button
          className="back-to-login"
          type="button"
          onClick={() => navigate("/login")}
        >
          Back to Login
        </button>
      </div>
    );
  }

  if (succeeded) {
    return (
      <div className="forgot-password-container reset-password-page">
        {heroBadge}
        <h1>Password Updated</h1>
        <div className="auth-inline-message is-success">
          Your password has been reset successfully.
        </div>
        <button
          type="button"
          className="reset-btn shimmer-button"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="forgot-password-container reset-password-page">
      {heroBadge}
      <h1>Reset Password</h1>
      <p>Choose a new password for your account.</p>

      <form onSubmit={handleSubmit} noValidate>
        <input
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password"
          autoComplete="new-password"
          aria-label="New password"
          aria-invalid={tooShort ? "true" : "false"}
        />
        <input
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          autoComplete="new-password"
          aria-label="Confirm new password"
          aria-invalid={mismatch ? "true" : "false"}
        />

        <label className="reset-password-show-toggle">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
          />
          <span>Show password</span>
        </label>

        <button
          type="submit"
          className="reset-btn shimmer-button"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save New Password"}
        </button>
      </form>

      {tooShort && (
        <div className="auth-inline-message is-error" aria-live="polite">
          Password must be at least {MIN_PASSWORD_LENGTH} characters.
        </div>
      )}
      {!tooShort && mismatch && (
        <div className="auth-inline-message is-error" aria-live="polite">
          Passwords do not match.
        </div>
      )}
      {errorMessage && (
        <div className="auth-inline-message is-error">{errorMessage}</div>
      )}

      <button
        className="back-to-login"
        type="button"
        onClick={() => navigate("/login")}
      >
        Back to Login
      </button>
    </div>
  );
};

export default ResetPassword;
